import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AumicoCpmmModule } from '@modeso/aumico-lib-cpmm-fe';
import { AumicoLibIntegrationsFeModule } from '@modeso/aumico-lib-integrations-fe';
import { I18NextModule } from 'angular-i18next';
import { LucideAngularModule } from 'lucide-angular';
import { AumicoComponentsModule } from '../../../../../../aumico-components/src/public-api';
import { AumicoLayoutModule2 } from '../../../../../../aumico-layout/src/lib';
import { AumicoCoreModule } from '../../../../../../aumico-lib-core-fe/src/lib';
import { UsersModule } from '../../../../../../aumico-lib-users-fe/src/public-api';
import { PageNotFoundComponent } from '../../../components/page-not-found/page-not-found.component';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { AdminGuard } from '../../../shared/admin.guard';
import { CanDeactivateGuard } from '../../../shared/can-deactivate.guard';
import { FCPGuard } from '../../../shared/fcp-guard.guard';
import { OrganizationProfilePage } from '../organization-profile/organization-profile.page';
import { OrganizationUsersPage } from '../organization-users/organization-users.page';
import { AddEditChecklistPage } from './check-list-management/add-edit-checklist/add-edit-checklist.component';
import { CheckListManagementPage } from './check-list-management/check-list-management.page';
import { ClosingWorkflowConfiguratorPage } from './closing-workflow-configurator/closing-workflow-configurator.page';
import { ClosingWorkflowOverviewPage } from './closing-workflow-overview/closing-workflow-overview.page';
import { MandateManagementConfiguratorPage } from './mandate-management-configurator/mandate-management-configurator.page';
import { SettingsPageComponent } from './settings/settings.page';
import { SubscriptionPage } from './subscription/subscription.page';
import { TextSnippetsPageComponent } from './text-snippets/text-snippets.page';
import { Organization2faPage } from './organization-2fa/organization-2fa.page';
import { AiSettingsPage } from './ai-settings/ai-settings.page';
import { IntegrationsPage } from './integrations/integrations.page';

const routes: Routes = [
    {
        path: '',
        children: [
            { path: '', redirectTo: `${Paths.TEXT_SNIPPETS}`, pathMatch: 'full' },
            {
                path: `${Paths.TEXT_SNIPPETS}`,
                component: TextSnippetsPageComponent,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.TEXT_SNIPPETS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.MANDATE_MANAGEMENT}`,
                component: MandateManagementConfiguratorPage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.MANDATE_MANAGEMENT,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.CLOSING_WORKFLOW_OVERVIEW}`,
                component: ClosingWorkflowOverviewPage,
                canActivate: [AdminGuard],
                data: {
                    label: RouteLabels.CLOSING_WORKFLOW,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.CLOSING_WORKFLOW_NEW}`,
                component: ClosingWorkflowConfiguratorPage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.CLOSING_WORKFLOW,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.CLOSING_WORKFLOW_ID}`,
                component: ClosingWorkflowConfiguratorPage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.CLOSING_WORKFLOW,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },

            {
                path: `${Paths.CHECKLISTS}`,
                component: CheckListManagementPage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.CHECKLISTS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.CHECKLISTS}/${Paths.ADD}`,
                component: AddEditChecklistPage,
                canActivate: [AdminGuard, FCPGuard],
                canDeactivate: [CanDeactivateGuard],
            },
            {
                path: `${Paths.CHECKLISTS}/${Paths.EDIT}/:id`,
                component: AddEditChecklistPage,
                canActivate: [AdminGuard, FCPGuard],
                canDeactivate: [CanDeactivateGuard],
            },
            {
                path: `${Paths.SETTINGS}`,
                component: SettingsPageComponent,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.DESIGN_SETTINGS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_USERS}`,
                component: OrganizationUsersPage,
                canActivate: [AdminGuard],
                data: {
                    label: RouteLabels.ORGANIZATION_USERS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_PROFILE}`,
                component: OrganizationProfilePage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.ORGANIZATION_PROFILE,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_USERS}`,
                component: OrganizationUsersPage,
                canActivate: [AdminGuard],
                data: {
                    label: RouteLabels.ORGANIZATION_USERS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_PROFILE}`,
                component: OrganizationProfilePage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.ORGANIZATION_PROFILE,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_SUBSCRIPTION}`,
                component: SubscriptionPage,
                canActivate: [AdminGuard],
                canDeactivate: [CanDeactivateGuard],
                data: {
                    label: RouteLabels.ORGANIZATION_SUBSCRIPTION,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.ORGANIZATION_2FA}`,
                component: Organization2faPage,
                canActivate: [AdminGuard],
                data: { label: RouteLabels.TWO_FACTOR_AUTHENTICATION, canSeeNavigationLinkRoles: ['AnnualReportUser'] },
            },
            {
                path: `${Paths.AI_SETTINGS}`,
                component: AiSettingsPage,
                canActivate: [AdminGuard],
                data: {
                    label: RouteLabels.AI_SETTINGS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.INTEGRATIONS}`,
                component: IntegrationsPage,
                canActivate: [AdminGuard],
                data: {
                    label: RouteLabels.INTEGRATIONS,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            { path: '**', component: PageNotFoundComponent },
        ],
    },
];

@NgModule({
    imports: [
        CommonModule,
        I18NextModule,
        RouterModule.forChild(routes),
        AumicoCoreModule,
        AumicoLayoutModule2,
        UsersModule,
        AumicoComponentsModule,
        AumicoCpmmModule,
        AumicoLibIntegrationsFeModule,
        LucideAngularModule,
    ],
    declarations: [
        CheckListManagementPage,
        AddEditChecklistPage,
        SettingsPageComponent,
        MandateManagementConfiguratorPage,
        SubscriptionPage,
        Organization2faPage,
        AiSettingsPage,
        IntegrationsPage,
    ],
})
export class OrganizationSettingsModule { }
